import { type FC } from 'react';
import { graphql, useFragment } from 'react-relay';
import { SeoLink } from 'dibs-seo/exports/SeoLink.tsx';
import HpLazyImageWrapper from '../HpLazyImageWrapper/HpLazyImageWrapper.tsx';
import ResponsiveImage from '../ResponsiveImage.tsx';

import dibsCss from 'dibs-css';
import classnames from 'classnames';
import styles from './HpSharedCollectionsModuleItem.scss';

import { type HpSharedCollectionsModuleItem_item$key } from './__generated__/HpSharedCollectionsModuleItem_item.graphql.ts';

const itemFragment = graphql`
    fragment HpSharedCollectionsModuleItem_item on Product {
        title
        imageUrl
        linkData {
            ...SeoLink_linkData
        }
    }
`;

type Props = {
    item: HpSharedCollectionsModuleItem_item$key;
    useLazyLoadImages: boolean;
    onClick: () => void;
};

export const HpSharedCollectionsModuleItem: FC<Props> = ({
    item: itemRef,
    onClick,
    useLazyLoadImages,
}) => {
    const item = useFragment(itemFragment, itemRef);

    if (!item) {
        return null;
    }

    const { title, imageUrl, linkData } = item;

    return (
        <SeoLink
            linkData={linkData}
            className={classnames(styles.itemWrapper, dibsCss.wFull, dibsCss.hFull, dibsCss.block)}
            onClick={onClick}
            dataTn="collections-item-link"
            buyerLinkType="linkNoDecoration"
        >
            <HpLazyImageWrapper useLazyLoad={useLazyLoadImages} className={styles.imageWrapper}>
                <ResponsiveImage
                    className={classnames(styles.image, dibsCss.objectCover, dibsCss.wFull)}
                    src={imageUrl || ''}
                    // no need for alt if title is present bc labeling the img with title is
                    // redundant with title as link text. only use alt if != title.
                    ariaHidden={!!title}
                    dataTn="collections-item-image"
                    srcSetWidths={[350]}
                    quality={80}
                />
            </HpLazyImageWrapper>
            <span
                className={classnames(
                    dibsCss.sassyFontBodyTypeHeavy,
                    dibsCss.block,
                    dibsCss.mtSmaller
                )}
                data-tn="collections-item-title"
            >
                {title}
            </span>
        </SeoLink>
    );
};
